// Idle lock overlay (board system/lock--idle, decision ردّ ٦): after the idle
// lock (node: erp-core `idle_lock_seconds`; cloud: 15 minutes) the screen dims
// + blurs; what is underneath stays mounted (an open till or invoice is not
// lost) but is inert. The PIN resumes the SAME session (node: POST
// /v1/erp/auth/unlock); «بدّل المستخدم» signs out so a colleague can sign in.
// No automatic sign-out. No coral element: the PIN submits on the last digit.
//
// States: entering · verifying · wrong PIN (403 invalid_pin) · locked out
// (429 pin_locked + Retry-After: input disabled until it ends) · session ended
// (401 → the login screen with the reason) · offline (network error copy).

import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/Avatar';
import { AuthError, AUTH_COPY, sessionStore, signOut, unlock } from '@/lib/auth';
import { formatTime } from '@/lib/format';
import { idleLockMs, lockedAfterTitle, lockStore } from '@/lib/idle-lock';
import { useSyncStatus } from '@/lib/offline-queue';
import { PinInput, PIN_LENGTH } from './PinInput';

export function useLocked(): boolean {
  return useSyncExternalStore(lockStore.subscribe, lockStore.isLocked, lockStore.isLocked);
}

export function LockScreen() {
  const locked = useLocked();
  if (!locked) return null;
  return <LockDialog />;
}

/**
 * Everything except the lock (and its ancestors) becomes inert + hidden from
 * assistive tech while locked; restored exactly on unlock.
 */
function useInertOutside(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const touched: Array<{ node: Element; inert: string | null; hidden: string | null }> = [];
    for (let node: Element | null = el; node && node !== document.body && node.parentElement; node = node.parentElement) {
      for (const sib of Array.from(node.parentElement.children)) {
        if (sib === node || sib.tagName === 'SCRIPT') continue;
        touched.push({ node: sib, inert: sib.getAttribute('inert'), hidden: sib.getAttribute('aria-hidden') });
        sib.setAttribute('inert', '');
        sib.setAttribute('aria-hidden', 'true');
      }
    }
    return () => {
      for (const t of touched) {
        if (t.inert == null) t.node.removeAttribute('inert');
        else t.node.setAttribute('inert', t.inert);
        if (t.hidden == null) t.node.removeAttribute('aria-hidden');
        else t.node.setAttribute('aria-hidden', t.hidden);
      }
    };
  }, [ref]);
}

/**
 * Keys never reach the page under the lock (shortcuts, Esc closing a dialog…).
 * A key pressed with focus outside the card moves focus back to the PIN.
 */
function useSwallowKeysOutside(ref: RefObject<HTMLElement>, refocus: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) {
        // Inside the lock: only stop the page's window-level handlers.
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
      if (e.key !== 'Tab') e.preventDefault();
      refocus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [ref, refocus]);
}

function LockDialog() {
  const navigate = useNavigate();
  const user = sessionStore.getUser();
  const sync = useSyncStatus();
  const idleSeconds = useSyncExternalStore(sessionStore.subscribe, sessionStore.getIdleLockSeconds, sessionStore.getIdleLockSeconds);
  const scrimRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLInputElement>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const reason = lockStore.reason();

  const refocus = useCallback(() => pinRef.current?.focus(), []);
  useInertOutside(scrimRef);
  useSwallowKeysOutside(scrimRef, refocus);

  // Focus returns to whatever had it before the lock.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    pinRef.current?.focus();
    return () => {
      if (prev && prev.isConnected) prev.focus?.();
    };
  }, []);

  // Lockout: the PIN boxes stay disabled until Retry-After passes.
  useEffect(() => {
    if (lockedUntil == null) return;
    const ms = lockedUntil - Date.now();
    const t = setTimeout(() => {
      setLockedUntil(null);
      setError(null);
    }, Math.max(0, ms));
    return () => clearTimeout(t);
  }, [lockedUntil]);
  useEffect(() => {
    if (lockedUntil == null && !busy) pinRef.current?.focus();
  }, [lockedUntil, busy]);

  const submit = async (value: string) => {
    if (value.length !== PIN_LENGTH || busy || lockedUntil != null) return;
    setBusy(true);
    setError(null);
    try {
      await unlock(value);
      setPin('');
    } catch (e) {
      setPin('');
      if (e instanceof AuthError && e.code === 'session_ended') {
        await signOut();
        navigate('/auth/login', { replace: true, state: { notice: e.message } });
        return;
      }
      if (e instanceof AuthError && e.code === 'locked' && e.retryAfterSeconds) {
        setLockedUntil(Date.now() + e.retryAfterSeconds * 1000);
      }
      setError(e instanceof AuthError ? e.message : AUTH_COPY.unexpected);
    } finally {
      setBusy(false);
    }
  };

  const title = reason === 'idle' ? lockedAfterTitle(idleLockMs(idleSeconds)) : 'مقفل';
  const meta = [user?.name, sync.lastSyncAt ? `آخر مزامنة ${formatTime(sync.lastSyncAt, { suffix: false })}` : null].filter(Boolean).join(' · ');

  return (
    <div ref={scrimRef} className="e-lock-scrim" role="dialog" aria-modal="true" aria-labelledby="lock-title" data-testid="lock-screen">
      <div className="e-card-auth e-lock-card">
        <span style={{ display: 'flex', justifyContent: 'center' }}>
          <Avatar name={user?.name} size={56} />
        </span>
        <div>
          <h1 id="lock-title" style={{ font: '700 20px/1.3 var(--font-ar)' }}>
            {title}
          </h1>
          {meta && <p className="muted" style={{ font: '500 13px/1.5 var(--font-ar)' }}>{meta}</p>}
        </div>
        <PinInput
          ref={pinRef}
          value={pin}
          onChange={setPin}
          onComplete={(p) => void submit(p)}
          label="الرمز السري"
          disabled={busy || lockedUntil != null}
          invalid={!!error}
        />
        {busy && (
          <p role="status" className="sr-only">
            جارٍ التحقق…
          </p>
        )}
        {error && (
          <p role="alert" data-testid="lock-error" style={{ color: 'var(--error)', font: '600 13px/1.4 var(--font-ar)' }}>
            {error}
          </p>
        )}
        <p style={{ font: '500 12px/1.4 var(--font-ar)', color: 'var(--mist)' }}>
          أدخل الرمز السري لمتابعة الجلسة · أو{' '}
          <button
            type="button"
            className="e-link e-lock-switch"
            data-testid="lock-switch-user"
            onClick={async () => {
              await signOut();
              navigate('/auth/login', { replace: true });
            }}
          >
            بدّل المستخدم
          </button>
        </p>
      </div>
    </div>
  );
}
